/**
 * Таймер: обратный отсчёт и секундомер.
 *
 * ГЛАВНОЕ ЗДЕСЬ — ОН НЕ ТИКАЕТ ПО СЕТИ. На сцене лежит МЕТКА
 * («до 19:30:00 по серверу»), а не остаток («осталось 300 секунд»),
 * и каждый оверлей считает от неё сам (docs/04).
 *
 * Почему не тикать сообщениями, хотя так проще:
 *
 *   шестьдесят сообщений в минуту НА КАЖДЫЙ таймер — это трафик
 *     и работа сервера ради числа, которое клиент вычислит сам;
 *   каждое сообщение может опоздать или потеряться — и тогда
 *     таймер замирает на секунду или прыгает через две;
 *   переподключение посреди отсчёта оставило бы зрителя с замершим
 *     числом до следующего тика;
 *   два оверлея одного канала показывали бы разное — у них разная
 *     задержка сети.
 *
 * С меткой всё это не возникает вовсе: разрыв связи таймеру
 * не мешает, а после переподключения он уже показывает правильное.
 *
 * ВТОРОЕ: СЧИТАЕМ ОТ МЕТКИ, А НЕ ВЫЧИТАНИЕМ ПО ТИКУ. Вычитание
 * накапливает ошибку планировщика: `setInterval` не обязан
 * срабатывать ровно, а в свёрнутой вкладке его душат до раза
 * в секунду и реже. Через час такой таймер разошёлся бы с реальностью
 * на минуты. Пересчёт от метки самокорректируется: опоздавший тик
 * просто покажет верное число чуть позже.
 */
import { serverNow } from './clock.js';
import { registerFont } from './fonts.js';
import { whenFontsReady } from './fonts.js';
import { asRecord, numberInRange, oneOf, optionalString } from './props.js';
import { applyTextStyle, parseTextStyle } from './text.js';
const TYPE = 'timer';
const MODES = ['countdown', 'countup'];
const FORMATS = ['auto', 'mm:ss', 'hh:mm:ss'];
/**
 * Как часто пересчитывается надпись.
 *
 * Пять раз в секунду, а не шестьдесят: у таймера секундная точность,
 * и крутить его на частоте кадров значит тратить процессор машины
 * стримера во время эфира. И не раз в секунду: тик, не совпадающий
 * с границей секунды, задерживал бы смену цифры почти на секунду —
 * заметно на последних числах отсчёта.
 *
 * В DOM при этом пишем ТОЛЬКО когда строка изменилась: запись
 * в textContent — это работа для браузера, даже когда значение
 * то же самое.
 */
export const TICK_MS = 200;
/** Предел длины подписи у числа. */
const MAX_SUFFIX = 40;
export const timerWidget = {
    type: TYPE,
    parse(raw) {
        const props = asRecord(TYPE, raw);
        return {
            ...parseTextStyle(TYPE, props),
            mode: oneOf(TYPE, props, 'mode', MODES, 'countdown'),
            // Метка — обычное время эпохи. Пределы не от педантизма:
            // отрицательное или бесконечное значение дало бы надпись
            // вроде `NaN:NaN`, и в эфире это выглядит поломкой оверлея
            at: numberInRange(TYPE, props, 'at', 0, MAX_EPOCH_MS, 0),
            paused: props.paused === true,
            frozenMs: numberInRange(TYPE, props, 'frozenMs', 0, MAX_SPAN_MS, 0),
            format: oneOf(TYPE, props, 'format', FORMATS, 'auto'),
            suffix: optionalString(TYPE, props, 'suffix', MAX_SUFFIX),
        };
    },
    mount(props) {
        const el = document.createElement('div');
        el.dataset.widget = TYPE;
        const value = document.createElement('span');
        value.dataset.role = 'value';
        el.append(value);
        const suffix = document.createElement('span');
        suffix.dataset.role = 'suffix';
        el.append(suffix);
        let current = props;
        let shown = '';
        registerFont({ family: props.font, src: props.fontSrc });
        const draw = () => {
            const next = format(current, serverNow());
            // Только при изменении: запись в textContent — работа
            // для браузера, даже когда значение то же самое, а тиков
            // здесь пять в секунду
            if (next !== shown) {
                shown = next;
                value.textContent = next;
            }
        };
        apply(el, suffix, current);
        draw();
        // ЕДИНСТВЕННЫЙ ИСТОЧНИК ДВИЖЕНИЯ — местный таймер. Ни здесь,
        // ни где-либо ещё у виджета нет доступа к сокету: контракт
        // виджета его не даёт, и это не оплошность, а защита
        const tick = setInterval(draw, TICK_MS);
        // Готовность шрифтов меняет ширину цифр: без перерисовки
        // надпись осталась бы в запасном шрифте до следующей смены
        // секунды — то есть могла бы застыть навсегда на паузе
        const unwatch = whenFontsReady(() => {
            applyTextStyle(el, current);
        });
        return {
            el,
            update(next) {
                current = next;
                registerFont({ family: next.font, src: next.fontSrc });
                apply(el, suffix, next);
                draw();
            },
            destroy() {
                // Не оставить таймер после уничтожения обязаны мы: слой,
                // ушедший за кадр, продолжал бы будить процессор пять раз
                // в секунду до конца эфира
                clearInterval(tick);
                unwatch();
                el.remove();
            },
        };
    },
    /**
     * Заводит шрифт, не создавая слоя.
     *
     * Отсчёт при этом НЕ начинается: он и не начинался — таймер
     * считает от метки, и до вывода в кадр считать некому и незачем.
     */
    preload(props) {
        registerFont({ family: props.font, src: props.fontSrc });
    },
};
/** Верхняя граница метки: год 3000 с запасом. */
const MAX_EPOCH_MS = 32_503_680_000_000;
/** Верхняя граница промежутка: сто дней. */
const MAX_SPAN_MS = 100 * 24 * 60 * 60 * 1000;
/**
 * Сколько показывать в этот момент.
 *
 * Отдельной функцией, потому что это единственное место, где
 * решается, что увидит зритель, — и проверять его надо без DOM
 * и без таймеров.
 */
export function format(props, now) {
    return span(remaining(props, now), props.format);
}
/**
 * Сколько осталось (или прошло), мс.
 *
 * ОТСЧЁТ НЕ УХОДИТ В МИНУС. Таймер, показывающий «-00:07», зритель
 * прочитает как поломку, а не как «перерыв затянулся»: у нас это
 * пятиминутный перерыв, а не соревнование. Дошедший до нуля стоит
 * на нуле, пока модератор не задаст новую метку.
 */
export function remaining(props, now) {
    if (props.paused)
        return Math.max(0, props.frozenMs);
    const delta = props.mode === 'countdown' ? props.at - now : now - props.at;
    return Math.max(0, delta);
}
/**
 * Переводит миллисекунды в надпись.
 *
 * ОКРУГЛЕНИЕ ВВЕРХ для остатка — не мелочь. При округлении вниз
 * таймер показывал бы «00:00» целую последнюю секунду, то есть
 * врал бы ровно там, где на него смотрят: в конце отсчёта.
 */
function span(ms, wanted) {
    const total = Math.ceil(ms / 1000);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    // `auto` показывает часы, только когда они есть: «00:04:59»
    // на пятиминутном перерыве занимает место и читается хуже
    const withHours = wanted === 'hh:mm:ss' || (wanted === 'auto' && hours > 0);
    if (withHours) {
        return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    }
    // Часы, не влезшие в формат, ПЕРЕЛИВАЮТСЯ В МИНУТЫ, а не
    // отбрасываются: «05:00» вместо «65:00» — это молчаливая ложь
    // про час с лишним
    return `${pad(hours * 60 + minutes)}:${pad(seconds)}`;
}
function pad(value) {
    return value < 10 ? `0${value}` : String(value);
}
function apply(el, suffix, props) {
    applyTextStyle(el, props);
    /* eslint-disable no-param-reassign --
       Правило бережёт неизменяемость СОСТОЯНИЯ, а здесь меняется DOM —
       ровно то, ради чего функция и существует. */
    if (suffix.textContent !== props.suffix) {
        suffix.textContent = props.suffix;
    }
    // Отступ появляется только вместе с подписью: пустой span
    // с полем сдвигал бы число вбок ни за чем
    suffix.style.marginLeft = props.suffix === '' ? '0' : '0.4em';
    /* eslint-enable no-param-reassign */
}
//# sourceMappingURL=timer.js.map